<template>
  <div class="rs-mcp">
    <!-- Banner Section -->
    <section class="banner-section">
      <div class="banner-header">
        <div class="banner-text">
          <h1 class="banner-title">
            AI assistants can now
            speak cricket fluently.
          </h1>
          <p class="banner-desc">
            Cricket API for MCP gives AI assistants live
            access to real cricket data.
            <br class="desktop-br" />
            So they stop guessing and start knowing.
          </p>
        </div>
        <a :href="calUrl" target="_blank" class="banner-cta" @click="onBannerCTAClick">
          <span>Book a Free 15-Min Demo</span>
          <svg
            class="cta-arrow"
            width="4"
            height="8"
            viewBox="0 0 4 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0.5 0.5L3.5 4L0.5 7.5"
              stroke="white"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </a>
      </div>
      <div class="banner-image animate-on-scroll">
        <!-- placeholder: replace with actual image -->
        <img
          src="https://assets.sports.dev.roanuz.com/ants/mcp/hero-banner.png"
          alt="MCP Demo"
          class="hero-img"
        />
      </div>
      <div class="ai-tools animate-on-scroll">
        <p class="ai-tools-label">
          <span class="gray">Works with</span>
          <span class="white"> any MCP client</span>
        </p>
        <div class="ai-tools-icons">
          <div class="tool-icon">
            <img src="https://assets.sports.dev.roanuz.com/ants/mcp/claude.svg" alt="Claude" />
          </div>
          <div class="tool-icon">
            <img src="https://assets.sports.dev.roanuz.com/ants/mcp/cursor.svg" alt="Cursor" />
          </div>
          <div class="tool-icon">
            <img src="https://assets.sports.dev.roanuz.com/ants/mcp/openai.svg" alt="OpenAI" />
          </div>
          <div class="tool-icon">
            <img src="https://assets.sports.dev.roanuz.com/ants/mcp/visual-code.svg" alt="VS Code" />
          </div>
        </div>
      </div>
    </section>

    <!-- Imagine Section -->
    <section class="imagine-section animate-on-scroll">
      <p class="imagine-text">
        Imagine your app's AI knowing right now
        who's on <span class="underline">strike</span>,
        what the required
        <span class="underline">run rate</span> is,
        and how this bowler has performed in death
        <span class="underline">overs</span> this season.
      </p>
    </section>

    <!-- Key Features Section -->
    <section class="key-features">
      <h2 class="key-features-title animate-on-scroll">
        Give your AI a full
        cricket brain.
      </h2>
      <div class="features-grid">
        <div
          class="features-row"
          v-for="(row, ri) in featureRows"
          :key="ri"
        >
          <div
            class="feature-line animate-on-scroll stagger-item"
            v-for="(item, ci) in row"
            :key="ci"
          >
            <span class="feature-label">{{ item }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- Use Cases Section -->
    <section class="use-cases">
      <h2 class="use-cases-title animate-on-scroll">
        Build smarter cricket
        experiences.
      </h2>
      <div class="use-cases-grid">
        <div
          class="use-case-card animate-on-scroll stagger-item"
          v-for="(uc, idx) in useCases"
          :key="idx"
        >
          <div class="use-case-text">
            <h3 class="use-case-name">{{ uc.title }}</h3>
            <p class="use-case-desc">{{ uc.desc }}</p>
          </div>
          <div class="use-case-image">
            <img
              :src="uc.img"
              :alt="uc.title"
              class="use-case-img"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Marquee Section -->
    <section class="marquee-section">
      <div
        class="marquee-row"
        v-for="(row, ri) in marqueeRows"
        :key="ri"
        :class="'marquee-row--' + ri"
      >
        <div class="marquee-track">
          <span
            class="marquee-item"
            v-for="(item, ci) in row.concat(row)"
            :key="ci"
          >
            <span class="marquee-text">{{ item }}</span>
            <span class="marquee-dot"></span>
          </span>
        </div>
      </div>
    </section>

    <!-- Trusted Section -->
    <section class="trusted-section">
      <div class="trusted-grid">
        <div class="trusted-left animate-on-scroll">
          <span class="trusted-label">
            Why Roanuz Cricket API
          </span>
          <h2 class="trusted-title">
            Trusted cricket data.
            <br />
            Now AI-ready.
          </h2>
        </div>
        <div class="trusted-right">
          <div class="trusted-stat trusted-stat--wide">
            <span class="stat-number">400+</span>
            <span class="stat-label">
              Tournaments covered
            </span>
          </div>
          <div class="trusted-stat-row">
            <div class="trusted-stat">
              <span class="stat-number">300ms</span>
              <span class="stat-label">
                Average latency
              </span>
            </div>
            <div class="trusted-stat">
              <span class="stat-number">99.75%</span>
              <span class="stat-label">uptime</span>
            </div>
          </div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
import { PageMetaData } from '@/core/meta';
import { allMetaMixins } from '@/mixin/meta';
import Config from '@/config';

const chatbotImg = 'https://assets.sports.dev.roanuz.com/ants/mcp/chatbot.png';
const newsroomImg = 'https://assets.sports.dev.roanuz.com/ants/mcp/newsroom.png';
const fantasyImg = 'https://assets.sports.dev.roanuz.com/ants/mcp/fantasy.png';

export default {
  name: 'RsMCP',
  mixins: allMetaMixins(),
  title() {
    return 'Cricket API for MCP | Live Scores, Stats & Fantasy Data for AI Assistants';
  },
  pageMetaData() {
    return new PageMetaData(
      'Cricket API for MCP | Live Scores, Stats & Fantasy Data for AI Assistants',
      'Connect AI assistants to real-time cricket data with Roanuz Cricket API for MCP. '
        + 'Live scores, ball-by-ball commentary, player stats, fantasy points & more. '
        + 'Works with Claude, Cursor, OpenAI, VS Code. 400+ tournaments, 99.75% uptime.',
      {
        image: 'https://www.cricketapi.com/img/hero-banner.png',
        twitterCard: 'summary_large_image',
        url: `${Config.appUrlPrefix}/mcp/`,
      },
    );
  },
  data() {
    return {
      isMobile: false,
      prefersReducedMotion: false,
      observers: [],
      marqueeRows: [
        [
          'AI Cricket Assistant',
          'Fan Engagement',
          'Post-Match Summary',
          'Match Prediction Tool',
          'Player Form Analyser',
        ],
        [
          'Post-Match Summary',
          'Cricket Trivia',
          'Live Score Chatbot',
          'Second Screen Experience',
          'Sports Broadcasting Assistant',
        ],
        [
          'Cricket App Copilot',
          'Real-Time Commentary Assistant',
          'Team Builder Assistant',
          'Player Form Analyser',
        ],
      ],
      useCases: [
        {
          title: 'AI Powered Cricket Chatbots',
          desc: 'Build a conversational cricket companion'
            + ' that fans can actually talk to about live'
            + ' scores, player history, and match'
            + ' predictions.',
          img: chatbotImg,
        },
        {
          title: 'AI Sports Newsrooms',
          desc: 'Power your newsroom with live, accurate'
            + ' cricket data. Draft match previews,'
            + ' post-match reports, and player profiles'
            + ' in seconds',
          img: newsroomImg,
        },
        {
          title: 'Fantasy Sports AI Assistants',
          desc: 'Give fantasy players an AI edge.'
            + ' Real-time player form, weather conditions,'
            + ' head-to-heads, and playing XI updates',
          img: fantasyImg,
        },
      ],
      featureRows: [
        [
          'Live Scores & Match Status',
          'Ball-by-Ball Commentary',
          'Over Summaries',
        ],
        [
          'Fixtures & Schedules',
          'Featured Matches',
          'Tournament Standings',
        ],
        [
          'Tournament Player Stats',
          'Tournament Team Stats',
          'Match Insights',
        ],
        [
          'Fantasy Credits',
          'Fantasy Points',
          'Match Odds',
        ],
      ],
    };
  },
  methods: {
    onBannerCTAClick() {
      if (this.$parent && this.$parent.trackCTAClick) {
        this.$parent.trackCTAClick('banner');
      }
    },
    initAnimations() {
      if (typeof window === 'undefined') return;
      this.prefersReducedMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (this.prefersReducedMotion) {
        // Show everything immediately for accessibility
        this.$el.querySelectorAll('.animate-on-scroll').forEach((el) => {
          el.classList.add('animated');
        });
        return;
      }
      this.isMobile = window.innerWidth < 768;
      this.$nextTick(() => {
        this.initScrollReveals();
      });
    },
    initScrollReveals() {
      if (typeof IntersectionObserver === 'undefined') return;
      this.$nextTick(() => {
        // Hide elements via JS so default CSS state is visible (safe fallback)
        this.$el.querySelectorAll('.animate-on-scroll').forEach((el) => {
          el.classList.add('scroll-hidden');
        });

        const revealElement = (target) => {
          if (target.classList.contains('stagger-item')) {
            const parent = target.parentElement;
            const siblings = parent
              ? Array.from(parent.querySelectorAll('.stagger-item'))
              : [target];
            const idx = siblings.indexOf(target);
            target.style.setProperty('--stagger-delay', `${idx * 100}ms`);
          }
          target.classList.remove('scroll-hidden');
          target.classList.add('animated');
          // Lock visible state with inline styles so it survives any class removal
          const onEnd = () => {
            target.style.opacity = '1';
            target.style.transform = 'none';
            target.removeEventListener('transitionend', onEnd);
          };
          target.addEventListener('transitionend', onEnd);
        };

        const observer = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              revealElement(entry.target);
              observer.unobserve(entry.target);
            }
          });
        }, { threshold: 0.15 });
        this.$el.querySelectorAll('.animate-on-scroll.scroll-hidden').forEach((el) => {
          observer.observe(el);
        });
        this.observers.push(observer);

        // Immediately reveal elements already in viewport
        requestAnimationFrame(() => {
          this.$el.querySelectorAll('.animate-on-scroll.scroll-hidden').forEach((el) => {
            const rect = el.getBoundingClientRect();
            if (rect.top < window.innerHeight && rect.bottom > 0) {
              revealElement(el);
              observer.unobserve(el);
            }
          });
        });
      });
    },
    cleanupAnimations() {
      this.observers.forEach((obs) => obs.disconnect());
      this.observers = [];
      if (this.$el && this.$el.querySelectorAll) {
        this.$el.querySelectorAll('.animate-on-scroll').forEach((el) => {
          el.classList.remove('animated', 'scroll-hidden');
          el.style.removeProperty('opacity');
          el.style.removeProperty('transform');
          el.style.removeProperty('--stagger-delay');
        });
      }
    },
    injectCanonical() {
      if (typeof document === 'undefined') return;
      let link = document.querySelector('link[rel="canonical"]');
      if (!link) {
        link = document.createElement('link');
        link.setAttribute('rel', 'canonical');
        document.head.appendChild(link);
      }
      link.setAttribute('href', 'https://www.cricketapi.com/mcp/');
    },
    injectStructuredData() {
      if (typeof document === 'undefined') return;
      const existing = document.querySelector('script[data-mcp-jsonld]');
      if (existing) return;
      const jsonLd = {
        '@context': 'https://schema.org',
        '@type': 'SoftwareApplication',
        name: 'Roanuz Cricket API for MCP',
        applicationCategory: 'DeveloperApplication',
        description: 'Connect AI assistants to real-time cricket data. '
          + 'Live scores, ball-by-ball commentary, player stats, fantasy points and more.',
        url: 'https://www.cricketapi.com/mcp/',
        operatingSystem: 'Any',
        offers: {
          '@type': 'Offer',
          price: '0',
          priceCurrency: 'USD',
          description: 'Free 15-minute demo available',
        },
        provider: {
          '@type': 'Organization',
          name: 'Roanuz Softwares Private Limited',
          url: 'https://www.cricketapi.com',
        },
        featureList: [
          'Live Scores & Match Status',
          'Ball-by-Ball Commentary',
          'Player Stats & Fantasy Points',
          '400+ Tournaments Covered',
          '99.75% Uptime',
          '300ms Average Latency',
          'Works with Claude, Cursor, OpenAI, VS Code',
        ],
      };
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.setAttribute('data-mcp-jsonld', 'true');
      script.textContent = JSON.stringify(jsonLd);
      document.head.appendChild(script);
    },
  },
  computed: {
    calUrl() {
      return this.$parent && this.$parent.calUrl ? this.$parent.calUrl : '#';
    },
  },
  activated() {
    // Re-init animations when component is reactivated (keep-alive / SPA back nav)
    this.cleanupAnimations();
    this.$nextTick(() => this.initAnimations());
  },
  mounted() {
    this.injectCanonical();
    this.injectStructuredData();
    this.initAnimations();
    // Handle bfcache restoration (back/forward navigation in Safari)
    if (typeof window !== 'undefined') {
      this.pageshowHandler = (event) => {
        if (event.persisted) {
          window.scrollTo(0, 0);
          this.cleanupAnimations();
          this.$nextTick(() => this.initAnimations());
        }
      };
      window.addEventListener('pageshow', this.pageshowHandler);
    }
  },
  beforeDestroy() {
    this.cleanupAnimations();
    if (typeof window !== 'undefined' && this.pageshowHandler) {
      window.removeEventListener('pageshow', this.pageshowHandler);
    }
    // Clean up canonical and structured data
    if (typeof document !== 'undefined') {
      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) canonical.remove();
      const jsonLd = document.querySelector('script[data-mcp-jsonld]');
      if (jsonLd) jsonLd.remove();
    }
  },
};
</script>

<style lang="scss" scoped>
.rs-mcp {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;

  h1, h2, h3, p {
    margin: 0;
    padding: 0;
  }

  .desktop-br {
    display: none;

    @media screen and (min-width: $breakpoint-md) {
      display: inline;
    }
  }

  // Banner Section
  .banner-section {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: size(40);
    padding: size(40) size(20) 0;
    max-width: size(1200);
    margin: 0 auto;
    width: 100%;
    box-sizing: border-box;

    > * {
      position: relative;
      z-index: 1;
    }

    @media screen and (min-width: $breakpoint-md) {
      gap: size(60);
      padding: size(40) size(40) 0;
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: size(80);
      padding: size(100) 0 0;
    }
  }

  @keyframes fadeInUp {
    from {
      opacity: 0;
      transform: translateY(30px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .banner-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    animation: fadeInUp 0.6s ease-out forwards;
    gap: size(20);
    max-width: size(800);
    width: 100%;

    @media screen and (min-width: $breakpoint-md) {
      gap: size(30);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: size(40);
    }
  }

  .banner-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: size(16);
    text-align: center;
    color: var(--rs-white-color);

    @media screen and (min-width: $breakpoint-md) {
      gap: size(20);
    }
  }

  .banner-title {
    font-family: var(--rz-hero-font);
    font-weight: 800;
    font-size: size(36);
    line-height: size(44);
    color: var(--rs-white-color);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(52);
      line-height: size(60);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(64);
      line-height: size(72);
    }
  }

  .banner-desc {
    font-family: var(--rz-hero-font);
    font-weight: 400;
    font-size: size(16);
    line-height: size(24);
    color: var(--rs-white-color);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(18);
      line-height: size(28);
    }
  }

  .banner-cta {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: size(10);
    padding: size(14) size(20);
    background: var(--rs-cta-blue-color);
    border-radius: size(8);
    text-decoration: none;
    transition: opacity 0.2s ease;

    @media screen and (min-width: $breakpoint-md) {
      padding: size(16) size(24);
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(20) size(32);
    }

    &:hover {
      opacity: 0.9;
    }

    span {
      font-family: var(--rz-hero-font);
      font-weight: 600;
      font-size: size(13);
      line-height: normal;
      color: var(--rs-white-color);
      white-space: nowrap;

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(14);
      }
    }

    .cta-arrow {
      width: size(4);
      height: size(8);
      flex-shrink: 0;
    }
  }

  .banner-image {
    width: 100%;
    max-width: size(1200);
    mix-blend-mode: lighten;
    height: size(240);
    background: #1e1e1e;
    border-radius: size(12);

    @media screen and (min-width: $breakpoint-md) {
      height: size(400);
    }

    @media screen and (min-width: $breakpoint-lg) {
      height: size(600);
    }

    .hero-img {
      width: 100%;
      height: 100%;
      display: block;
      border-radius: size(12);
      object-fit: cover;
    }
  }

  .ai-tools {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: size(24);
    padding: size(24);
    width: 100%;
    box-sizing: border-box;

    @media screen and (min-width: $breakpoint-md) {
      gap: size(24);
    }
  }

  .ai-tools-label {
    font-family: var(--rz-hero-font);
    font-weight: bold;
    font-size: size(14);
    line-height: normal;
    text-align: center;

    .gray {
      color: #7d7d7d;
    }

    .white {
      color: var(--rs-white-color);
    }
  }

  .ai-tools-icons {
    display: flex;
    align-items: center;
    gap: size(10);
  }

  .tool-icon {
    width: size(56);
    height: size(56);
    aspect-ratio: 1/1;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;

    img {
      width: 100%;
      height: 100%;
    }
  }

  // Imagine Section
  .imagine-section {
    padding: size(20) size(20);
    margin-top: size(60);
    display: flex;
    justify-content: center;
    max-width: size(1200);
    width: 100%;
    box-sizing: border-box;

    @media screen and (min-width: $breakpoint-md) {
      padding: size(30) size(40);
      margin-top: size(80);
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(40) 0;
      margin-top: size(120);
    }
  }

  .imagine-text {
    font-family: var(--rz-hero-font);
    font-weight: 500;
    font-size: size(24);
    line-height: size(36);
    color: var(--rs-white-color);
    text-align: center;
    max-width: size(800);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(32);
      line-height: size(46);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(40);
      line-height: size(56);
    }

    .underline {
      text-decoration: underline;
    }
  }

  // Key Features Section
  .key-features {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: size(40);
    padding: 0 size(20) size(30);
    margin-top: size(60);
    max-width: size(1200);
    width: 100%;
    box-sizing: border-box;

    @media screen and (min-width: $breakpoint-md) {
      gap: size(50);
      padding: 0 size(40) size(40);
      margin-top: size(80);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: size(60);
      padding: 0 0 size(60);
      margin-top: size(120);
    }
  }

  .key-features-title {
    font-family: var(--rz-hero-font);
    font-weight: 800;
    font-size: size(32);
    line-height: size(42);
    color: var(--rs-white-color);
    text-align: center;
    max-width: size(600);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(40);
      line-height: size(52);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(48);
      line-height: size(64);
    }
  }

  .features-grid {
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: size(1200);
  }

  .features-row {
    display: flex;
    flex-direction: column;
    gap: 0;

    @media screen and (min-width: $breakpoint-md) {
      flex-direction: row;
      gap: size(20);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: size(40);
    }
  }

  .feature-line {
    flex: 1;
    padding: size(18) 0;
    border-bottom: size(1) solid #262626;
    overflow: hidden;

    @media screen and (min-width: $breakpoint-md) {
      padding: size(20) 0;
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(24) 0;
    }
  }

  .feature-label {
    font-family: var(--rz-hero-font);
    font-weight: 500;
    font-size: size(16);
    line-height: normal;
    color: var(--rs-white-color);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(18);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(20);
    }
  }

  // Use Cases Section
  .use-cases {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: size(40);
    padding: 0 size(20) size(30);
    margin-top: size(60);
    max-width: size(1200);
    width: 100%;
    box-sizing: border-box;

    @media screen and (min-width: $breakpoint-md) {
      gap: size(50);
      padding: 0 size(40) size(40);
      margin-top: size(80);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: size(60);
      padding: 0 0 size(60);
      margin-top: size(120);
    }
  }

  .use-cases-title {
    font-family: var(--rz-hero-font);
    font-weight: 800;
    font-size: size(32);
    line-height: size(42);
    color: var(--rs-white-color);
    text-align: center;
    max-width: size(600);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(40);
      line-height: size(52);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(48);
      line-height: size(64);
    }
  }

  .use-cases-grid {
    display: flex;
    flex-direction: column;
    width: 100%;
  }

  .use-case-card {
    display: flex;
    flex-direction: column;
    gap: size(20);
    border-top: size(1) solid #262626;
    padding: size(40) 0;

    @media screen and (min-width: $breakpoint-md) {
      flex-direction: row;
      gap: size(60);
    }

    @media screen and (min-width: $breakpoint-lg) {
      gap: size(100);
    }
  }

  .use-case-text {
    display: flex;
    flex-direction: column;
    gap: size(16);
    width: 100%;

    @media screen and (min-width: $breakpoint-md) {
      width: size(260);
      min-width: size(260);
      gap: size(20);
    }

    @media screen and (min-width: $breakpoint-lg) {
      width: size(340);
      min-width: size(340);
    }
  }

  .use-case-name {
    font-family: var(--rz-hero-font);
    font-weight: 700;
    font-size: size(20);
    line-height: size(28);
    color: var(--rs-white-color);
    padding-top: size(20);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(26);
      line-height: size(34);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(32);
      line-height: size(40);
    }
  }

  .use-case-desc {
    font-family: var(--rz-hero-font);
    font-weight: 400;
    font-size: size(16);
    line-height: size(24);
    color: #e8e8e8;

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(18);
      line-height: size(26);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(20);
      line-height: size(28);
    }
  }

  .use-case-image {
    flex: 1;
    min-height: size(200);
    border-radius: size(16);
    overflow: hidden;
    background: #1e1e1e;

    @media screen and (min-width: $breakpoint-md) {
      min-height: auto;
      height: size(400);
      border-radius: size(20);
    }

    @media screen and (min-width: $breakpoint-lg) {
      height: size(560);
    }

    .use-case-img {
      width: 100%;
      height: 100%;
      display: block;
      object-fit: cover;
      border-radius: size(16);

      @media screen and (min-width: $breakpoint-md) {
        border-radius: size(20);
      }
    }
  }

  // Marquee Section
  .marquee-section {
    display: flex;
    flex-direction: column;
    gap: size(20);
    padding: 0;
    margin-top: size(60);
    width: 100%;
    overflow: hidden;

    @media screen and (min-width: $breakpoint-md) {
      padding: 0;
      margin-top: size(80);
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: 0;
      margin-top: size(120);
    }
  }

  .marquee-row {
    display: flex;
    overflow: hidden;
    width: 100%;
  }

  .marquee-track {
    display: flex;
    align-items: center;
    white-space: nowrap;
    animation: marquee-scroll 60s linear infinite;
    will-change: transform;
    -webkit-backface-visibility: hidden;
    backface-visibility: hidden;
  }

  .marquee-row--1 .marquee-track {
    animation-direction: reverse;
    animation-duration: 70s;
  }

  .marquee-row--2 .marquee-track {
    animation-duration: 65s;
  }

  @keyframes marquee-scroll {
    0% {
      transform: translate3d(0, 0, 0);
    }
    100% {
      transform: translate3d(-50%, 0, 0);
    }
  }

  .marquee-item {
    display: flex;
    align-items: center;
    gap: size(20);
    flex-shrink: 0;
  }

  .marquee-text {
    font-family: var(--rz-hero-font);
    font-weight: 400;
    font-size: size(28);
    line-height: normal;
    color: #e8e8e8;
    white-space: nowrap;

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(34);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(40);
    }
  }

  .marquee-dot {
    display: block;
    width: size(10);
    height: size(10);
    border-radius: 50%;
    background: #e8e8e8;
    flex-shrink: 0;
    margin: 0 size(10);
  }

  // Trusted Section
  .trusted-section {
    max-width: size(1200);
    margin-top: size(60);
    width: 100%;
    padding: size(20) size(20);
    box-sizing: border-box;

    @media screen and (min-width: $breakpoint-md) {
      padding: size(30) size(40);
      margin-top: size(80);
    }

    @media screen and (min-width: $breakpoint-lg) {
      padding: size(40) 0;
      margin-top: size(120);
    }
  }

  .trusted-grid {
    display: flex;
    flex-direction: column;

    @media screen and (min-width: $breakpoint-md) {
      flex-direction: row;
    }
  }

  .trusted-left {
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    gap: size(10);
    padding: size(30) size(20);
    border: size(1) solid #262626;
    border-radius: size(20) size(20) 0 0;
    min-height: size(200);

    @media screen and (min-width: $breakpoint-md) {
      flex: 1;
      padding: size(40);
      border-radius: size(20) 0 0 size(20);
      min-height: size(400);
    }
  }

  .trusted-label {
    font-family: var(--rz-hero-font);
    font-weight: 400;
    font-size: size(14);
    line-height: size(20);
    color: #e8e8e8;
  }

  .trusted-title {
    font-family: var(--rz-hero-font);
    font-weight: 700;
    font-size: size(28);
    line-height: size(36);
    color: var(--rs-white-color);

    @media screen and (min-width: $breakpoint-md) {
      font-size: size(32);
      line-height: size(40);
    }

    @media screen and (min-width: $breakpoint-lg) {
      font-size: size(40);
      line-height: size(48);
    }
  }

  .trusted-right {
    display: flex;
    flex-direction: column;
    flex: 1;
  }

  .trusted-stat--wide {
    border: size(1) solid #262626;
    border-radius: 0;
    flex: 1;

    @media screen and (min-width: $breakpoint-md) {
      border-radius: 0 size(20) 0 0;
    }
  }

  .trusted-stat-row {
    display: flex;
    flex-direction: column;
    flex: 1;

    @media screen and (min-width: $breakpoint-md) {
      flex-direction: row;
    }

    .trusted-stat {
      flex: 1;

      &:first-child {
        border-radius: 0;
      }

      &:last-child {
        border-radius: 0 0 size(20) size(20);

        @media screen and (min-width: $breakpoint-md) {
          border-radius: 0 0 size(20) 0;
        }
      }
    }
  }

  .trusted-stat {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: size(10);
    padding: size(24) size(20);
    border: size(1) solid #262626;

    @media screen and (min-width: $breakpoint-md) {
      padding: size(30) size(40);
    }

    .stat-number {
      font-family: var(--rz-hero-font);
      font-weight: 700;
      font-size: size(28);
      line-height: size(36);
      color: var(--rs-white-color);

      @media screen and (min-width: $breakpoint-md) {
        font-size: size(32);
        line-height: size(40);
      }

      @media screen and (min-width: $breakpoint-lg) {
        font-size: size(40);
        line-height: size(48);
      }
    }

    .stat-label {
      font-family: var(--rz-hero-font);
      font-weight: 400;
      font-size: size(14);
      line-height: size(20);
      color: #e8e8e8;
    }
  }

  // Scroll Reveal Animations
  // Default state is visible (safe fallback if JS fails)
  .animate-on-scroll {
    transition: opacity 0.6s ease-out, transform 0.6s ease-out;
    transition-delay: var(--stagger-delay, 0ms);

    // JS adds this class to hide elements before they scroll into view
    &.scroll-hidden {
      opacity: 0;
      transform: translate3d(0, 30px, 0);
    }

    &.animated {
      opacity: 1;
      transform: translate3d(0, 0, 0);
    }
  }

  .feature-line.animate-on-scroll.scroll-hidden {
    transform: translate3d(0, 20px, 0);
  }

  .use-case-card.animate-on-scroll.scroll-hidden {
    transform: translate3d(0, 40px, 0);
  }

  .use-case-card.animate-on-scroll {
    transition-duration: 0.8s;
  }

  // Accessibility: respect reduced motion
  @media (prefers-reduced-motion: reduce) {
    .animate-on-scroll {
      opacity: 1 !important;
      transform: none !important;
      transition: none !important;
    }
  }
}
</style>
